/**
 * Correlation ID Utility
 * Generates and manages correlation IDs for request tracking
 */

/**
 * Generate a unique correlation ID
 * @returns {string} - UUID v4 format correlation ID
 */
export const generateCorrelationId = () => {
  // Generate UUID v4 format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Get current correlation ID from context or generate new one
 * @returns {string} - Current or new correlation ID
 */
export const getCurrentCorrelationId = () => {
  // Check if there's an active correlation ID in session storage
  const currentId = sessionStorage.getItem('currentCorrelationId');
  if (currentId) {
    return currentId;
  }
  
  // Generate new correlation ID
  const newId = generateCorrelationId();
  sessionStorage.setItem('currentCorrelationId', newId);
  return newId;
};

/**
 * Set correlation ID for current session
 * @param {string} correlationId - Correlation ID to set
 */
export const setCorrelationId = (correlationId) => {
  sessionStorage.setItem('currentCorrelationId', correlationId);
};

/**
 * Clear current correlation ID
 */
export const clearCorrelationId = () => {
  sessionStorage.removeItem('currentCorrelationId');
};

/**
 * Extract correlation ID from response headers
 * @param {Headers} headers - Response headers
 * @returns {string|null} - Correlation ID from response or null
 */
export const extractCorrelationIdFromHeaders = (headers) => {
  if (!headers) return null;
  
  // Try common correlation ID header names
  const headerNames = [
    'x-correlation-id',
    'X-Correlation-ID', 
    'correlation-id',
    'x-request-id',
    'request-id'
  ];
  
  for (const headerName of headerNames) {
    const value = headers.get(headerName);
    if (value) {
      return value;
    }
  }
  
  return null;
};